import { Controller } from "@hotwired/stimulus"

// Keeps the checkout address summary in sync with modal interactions.
export default class extends Controller {
  static targets = ["details", "empty", "line1", "line2", "area", "country", "companyContainer", "companyName", "vatNumber", "trigger"]

  static values = {
    state: String,
    selected: String,
    newUrl: String,
    listUrl: String
  }

  connect() {
    this.handleAddressSelected = this.onAddressSelected.bind(this)
    this.handleAddressCleared = this.onAddressCleared.bind(this)

    window.addEventListener("checkout-address:selected", this.handleAddressSelected)
    window.addEventListener("checkout-address:cleared", this.handleAddressCleared)

    const hasSelectedValue = typeof this.selectedValue === "string" && this.selectedValue.trim().length > 0

    if (hasSelectedValue) {
      const parsed = this.parseAddress(this.selectedValue)
      if (parsed) {
        this.renderAddress(parsed)
        return
      }
    }

    if (this.stateValue === "empty") {
      this.clearAddress()
    } else {
      // Ensure the trigger reflects the presence of an address even if no data attribute was provided.
      this.updateTriggerForState("change")
      this.showDetails()
    }
  }

  disconnect() {
    window.removeEventListener("checkout-address:selected", this.handleAddressSelected)
    window.removeEventListener("checkout-address:cleared", this.handleAddressCleared)
  }

  stateValueChanged(value, previous) {
    if (value === previous) return

    if (value === "empty") {
      this.renderEmptyState()
      this.updateTriggerForState("add")
    }
  }

  selectedValueChanged(value, previous) {
    if (value === previous) return

    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = this.parseAddress(value)
      if (parsed) {
        this.renderAddress(parsed)
        return
      }
    }

    this.renderEmptyState()
    this.updateTriggerForState("add")
  }

  onAddressSelected(event) {
    const detail = event.detail || {}
    if (!detail.address) return

    this.renderAddress(detail.address)
  }

  onAddressCleared() {
    this.clearAddress()
  }

  renderAddress(address) {
    const data = this.normaliseAddress(address)
    const line1 = this.joinSegments(data.line1)
    const line2 = this.joinSegments(data.line2)
    const area = this.joinSegments(data.suburb, data.city, data.province, data.postal_code || data.postalCode)
    const countryText = data.country ? `${data.country}`.trim() : ""
    const isBusiness = this.isTruthy(data.is_company ?? data.isCompany)
    const companyName = data.company_name || data.companyName || ""
    const vatNumber = data.vat_number || data.vatNumber || ""

    if (this.hasLine1Target) {
      this.line1Target.textContent = line1 || "—"
    }

    if (this.hasLine2Target) {
      if (line2) {
        this.line2Target.classList.remove("hidden")
        this.line2Target.textContent = line2
      } else {
        this.line2Target.classList.add("hidden")
        this.line2Target.textContent = ""
      }
    }

    if (this.hasAreaTarget) {
      this.areaTarget.textContent = area
    }

    if (this.hasCompanyContainerTarget) {
      if (isBusiness) {
        this.companyContainerTarget.classList.remove("hidden")
        if (this.hasCompanyNameTarget) {
          this.companyNameTarget.textContent = companyName || "Company name not provided"
        }
        if (this.hasVatNumberTarget) {
          if (vatNumber) {
            this.vatNumberTarget.classList.remove("hidden")
            this.vatNumberTarget.textContent = `VAT: ${vatNumber}`
          } else {
            this.vatNumberTarget.classList.add("hidden")
            this.vatNumberTarget.textContent = ""
          }
        }
      } else {
        this.companyContainerTarget.classList.add("hidden")
        if (this.hasCompanyNameTarget) {
          this.companyNameTarget.textContent = ""
        }
        if (this.hasVatNumberTarget) {
          this.vatNumberTarget.classList.add("hidden")
          this.vatNumberTarget.textContent = ""
        }
      }
    }

    if (this.hasCountryTarget) {
      if (countryText) {
        this.countryTarget.classList.remove("hidden")
        const span = this.countryTarget.querySelector("span")
        if (span) {
          span.textContent = countryText
        } else {
          this.countryTarget.textContent = countryText
        }
      } else {
        this.countryTarget.classList.add("hidden")
      }
    }

    this.showDetails()
    const payload = JSON.stringify(data)
    if (this.selectedValue !== payload) {
      this.selectedValue = payload
    }
    if (this.stateValue !== "present") {
      this.stateValue = "present"
    }
    this.updateTriggerForState("change")
  }

  clearAddress() {
    this.renderEmptyState()

    if (this.selectedValue !== "") {
      this.selectedValue = ""
    }
    if (this.stateValue !== "empty") {
      this.stateValue = "empty"
    }
    this.updateTriggerForState("add")
  }

  renderEmptyState() {
    if (this.hasDetailsTarget) {
      this.detailsTarget.classList.add("hidden")
    }

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.remove("hidden")
    }

    if (this.hasLine2Target) {
      this.line2Target.classList.add("hidden")
      this.line2Target.textContent = ""
    }

    if (this.hasCompanyContainerTarget) {
      this.companyContainerTarget.classList.add("hidden")
    }

    if (this.hasCompanyNameTarget) {
      this.companyNameTarget.textContent = ""
    }

    if (this.hasVatNumberTarget) {
      this.vatNumberTarget.classList.add("hidden")
      this.vatNumberTarget.textContent = ""
    }

    if (this.hasCountryTarget) {
      this.countryTarget.classList.add("hidden")
    }
  }

  showDetails() {
    if (this.hasDetailsTarget) {
      this.detailsTarget.classList.remove("hidden")
    }
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.add("hidden")
    }
  }

  updateTriggerForState(mode) {
    if (!this.hasTriggerTarget) return
    const trigger = this.triggerTarget

    if (mode === "change") {
      trigger.textContent = "Change"
      if (this.listUrlValue) trigger.href = this.listUrlValue
    } else {
      trigger.textContent = "Add address"
      if (this.newUrlValue) trigger.href = this.newUrlValue
    }
  }

  parseAddress(payload) {
    if (!payload || payload.trim() === "") return null

    try {
      // First try direct parsing (should work for normal cases)
      const parsed = JSON.parse(payload);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (firstError) {
      try {
        // Try decoding HTML entities (" -> " etc)
        let htmlDecoded = payload.replace(/"/g, '"').replace(/&/g, '&').replace(/</g, '<').replace(/>/g, '>');
        const parsed = JSON.parse(htmlDecoded);
        return parsed && typeof parsed === "object" ? parsed : null;
      } catch (secondError) {
        // Try unescaping backslashes
        try {
          const unescaped = payload.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
          const parsed = JSON.parse(unescaped);
          return parsed && typeof parsed === "object" ? parsed : null;
        } catch (thirdError) {
          console.error("All JSON parsing attempts failed. Raw:", payload);
          return null;
        }
      }
    }
  }

  normaliseAddress(address) {
    const data = { ...address }
    if (!data.postal_code && data.postalCode) data.postal_code = data.postalCode
    if (!data.postalCode && data.postal_code) data.postalCode = data.postal_code
    return data
  }

  isTruthy(value) {
    return value === true || value === "true" || value === 1 || value === "1"
  }

  joinSegments(...segments) {
    return segments
      .flatMap((value) => {
        if (value == null) return []
        const str = `${value}`.trim()
        return str.length > 0 ? [str] : []
      })
      .join(", ")
  }
}
